/**
 * SwissPass OAuth callback page (`/login/swisspass/callback`).
 *
 * Completes the authorization-code + PKCE flow after SwissPass redirects back:
 * validates `state`, exchanges `code` with the FlowRide backend, stores the
 * access token, then navigates to `/home` with a bottom toast for success or failure.
 */

import { IonContent, IonPage, IonSpinner, useIonToast } from "@ionic/react";
import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "../../components/Modal/Modal";
import {
    clearPkceTransient,
    loginWithSwissPass,
    peekAuthState,
    setAccessToken,
} from "../../services/auth";
import { usePersistenceReady } from "../../store/persistence";

/**
 * Handles SwissPass redirect query parameters and finishes the login handshake.
 *
 * Renders a non-dismissible spinner until the async exchange completes and
 * `history.replace("/home")` runs.
 *
 * @returns Full-page Ionic shell with an in-progress login modal.
 */
const SwissPassCallbackPage: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const persistenceReady = usePersistenceReady();
    const [ present ] = useIonToast();

    /**
     * Ensures the callback exchange runs only once per mount.
     * Needed because React Strict Mode may invoke effects twice in development,
     * and a second `loginWithSwissPass` would reuse a consumed authorization code.
     */
    const startedRef = useRef( false );

    useEffect( () => {
        // PKCE verifier / oauth state live in persisted storage; wait until hydrated.
        if ( !persistenceReady || startedRef.current ) {
            return;
        }
        startedRef.current = true;

        /**
         * Clears transient PKCE fields, shows a toast, and leaves the callback route.
         *
         * @param message - User-facing toast text.
         * @param color - Ionic toast color for success vs error.
         */
        const finish = ( message: string, color: "success" | "danger" ) => {
            clearPkceTransient();
            void present( {
                message,
                color,
                "duration": 2500,
                "position": "bottom",
            } );
            history.replace( "/home" );
        };

        const run = async() => {
            const params = new URLSearchParams( location.search );
            const error = params.get( "error" );

            // User denied consent or SwissPass returned an OAuth error.
            if ( error != null && error !== "" ) {
                finish( "Connexion SwissPass annulée ou refusée", "danger" );

                return;
            }

            const code = params.get( "code" );
            const state = params.get( "state" );
            const auth = peekAuthState();

            if ( code == null || code === "" || state == null || state === "" ) {
                finish( "Réponse de connexion invalide", "danger" );

                return;
            }

            // CSRF / session binding: redirect `state` must match what we stored pre-authorize.
            if ( auth.oauthState == null || auth.oauthState !== state ) {
                finish( "État de connexion invalide. Réessayez.", "danger" );

                return;
            }

            // Verifier is required for the backend PKCE token exchange; missing means restart login.
            if ( auth.pkceCodeVerifier == null || auth.pkceCodeVerifier === "" ) {
                finish( "Session de connexion expirée. Réessayez.", "danger" );

                return;
            }

            try {
                const response = await loginWithSwissPass( {
                    code,
                    "codeVerifier": auth.pkceCodeVerifier,
                } );
                setAccessToken( response.access_token );
                finish( "Connexion réussie", "success" );
            } catch {
                finish( "La connexion a échoué. Réessayez.", "danger" );
            }
        };

        void run();
    }, [ history, location.search, persistenceReady, present ] );

    return (
        <IonPage>
            <IonContent>
                <Modal
                    isOpen
                    onClose={() => {
                        // Spinner modal is not dismissible during callback processing.
                    }}
                >
                    <div className="flex flex-col items-center gap-4 py-4">
                        <IonSpinner name="crescent" />
                        <p className="text-center text-sm">
                            Connexion en cours…
                        </p>
                    </div>
                </Modal>
            </IonContent>
        </IonPage>
    );
};

export default SwissPassCallbackPage;
